import { useState, useRef, useEffect } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { X, Pen, Keyboard, Image, Trash2, Plus, ChevronLeft, Loader2, AlertCircle, Copy } from 'lucide-react';
import { useSavedSignatures } from '../hooks/useSavedSignatures';

const FONTS = [
  { name: 'Dancing Script', label: 'Elegant' },
  { name: 'Caveat', label: 'Casual' },
  { name: 'Satisfy', label: 'Smooth' },
  { name: 'Great Vibes', label: 'Formal' },
  { name: 'Homemade Apple', label: 'Handwritten' },
];

const INK_COLORS = [
  { value: '#000000', label: 'Black pen' },
  { value: '#1a4fb5', label: 'Blue pen' },
  { value: '#c62828', label: 'Red pen' },
];

const DRAW_BASE_WIDTH = 3.5;
const TYPE_STROKE_WIDTH = 3.5; // stored with type signatures for consistency

// ==================== Panel shell ====================

/**
 * @param {object} props
 * @param {(dataUrl: string) => void} props.onCreated
 * @param {() => void} props.onClose
 * @param {object} [props.savedSigs] — shared state from useSavedSignatures (lifted to Viewer).
 *   When omitted (e.g. Sign.jsx), the panel manages its own instance; callers
 *   that want list-sync with a sibling Sign dropdown should lift the hook.
 *   Callers MUST pass a hook instance scoped to the matching fieldKind — the
 *   panel will otherwise cross-contaminate the signatures and initials libraries.
 * @param {'list'|'create'} [props.initialView] — force a starting view; defaults to auto-pick.
 * @param {'signature'|'initials'} [props.fieldKind] — labels the dialog as
 *   "Signature" or "Initials" AND selects which saved library to read from /
 *   write to (Prompt 24.1 split).
 * @param {number} [props.applyAllCount] — how many fields of the current kind
 *   the signer has in this envelope. When >1, each saved library row exposes
 *   an "Apply to all" action; `onCreated` is called with `{ applyToAll: true }`.
 *   When unspecified or ≤1, the bulk action is hidden (Prompt 24.1 §2d).
 */
export default function SignaturePanel({ onCreated, onClose, savedSigs, initialView, fieldKind = 'signature', applyAllCount = 1 }) {
  const isInitials = fieldKind === 'initials';
  const noun = isInitials ? 'Initials' : 'Signature';
  const nounPlural = isInitials ? 'Initials' : 'Signatures';
  const navigate = useNavigate();
  const localSigs = useSavedSignatures(fieldKind);
  const effectiveSigs = savedSigs || localSigs;
  const { signatures, loading, signedIn, add, remove, capReached, cap, plan } = effectiveSigs;

  // Escape closes the overlay. Registered while mounted so stacked overlays
  // (the outer SignaturePanelOverlay wrapper from SignDocument) don't need to
  // bubble through a captured keydown themselves.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Guests go straight to Create. Signed-in users start in the library so
  // repeat use (the whole point of the feature) is one click. An explicit
  // initialView overrides both defaults (used by "+ New signature" entry).
  const [view, setView] = useState(() => {
    if (initialView) return initialView;
    return signedIn ? 'list' : 'create';
  });

  // Once the initial fetch resolves and the user has zero signatures, flip
  // into Create so the dialog isn't an empty shell. Only runs once per mount.
  const didAutoSwitch = useRef(false);
  useEffect(() => {
    if (initialView || !signedIn || loading || didAutoSwitch.current) return;
    if (signatures.length === 0) {
      setView('create');
      didAutoSwitch.current = true;
    }
  }, [initialView, signedIn, loading, signatures.length]);

  // Load signature fonts once (used by Type tab preview + render).
  useEffect(() => {
    const id = 'signature-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.href =
      'https://fonts.googleapis.com/css2?family=Caveat&family=Dancing+Script&family=Great+Vibes&family=Homemade+Apple&family=Satisfy&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);

  const handleUseSaved = (sig) => {
    onCreated(sig.dataUrl);
  };

  const handleUseSavedForAll = (sig) => {
    onCreated(sig.dataUrl, { applyToAll: true });
  };

  const [deleteError, setDeleteError] = useState(null);
  const handleDeleteSaved = async (e, id) => {
    e.stopPropagation();
    setDeleteError(null);
    try {
      await remove(id);
    } catch (err) {
      console.error('Delete signature failed:', err);
      setDeleteError(err.message || 'Could not delete signature.');
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
          <div className="flex items-center gap-2">
            {view === 'create' && signedIn && signatures.length > 0 && (
              <button
                onClick={() => setView('list')}
                className="p-1 rounded-md hover:bg-gray-100 text-gray-500"
                title={`Back to saved ${nounPlural.toLowerCase()}`}
              >
                <ChevronLeft size={18} />
              </button>
            )}
            <h2 className="text-lg font-bold text-gray-900">
              {view === 'list' ? nounPlural : `Create ${noun}`}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400">
            <X size={18} />
          </button>
        </div>

        {view === 'list' ? (
          <>
            {deleteError && (
              <div className="mx-5 mb-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                {deleteError}
              </div>
            )}
            <ListView
              signatures={signatures}
              loading={loading}
              capReached={capReached}
              cap={cap}
              plan={plan}
              noun={noun}
              nounPlural={nounPlural}
              applyAllCount={applyAllCount}
              onUse={handleUseSaved}
              onUseForAll={handleUseSavedForAll}
              onDelete={handleDeleteSaved}
              onNew={() => setView('create')}
            />
          </>
        ) : (
          <CreateView
            signedIn={signedIn}
            capReached={capReached}
            cap={cap}
            plan={plan}
            savedCount={signatures.length}
            noun={noun}
            onCreated={onCreated}
            onSave={add}
            onSignInClick={() => {
              onClose();
              navigate('/login');
            }}
          />
        )}
      </div>
    </div>
  );
}

// ==================== Saved signatures list ====================

function capMessage({ plan, cap }) {
  // Cap applies combined across signatures + initials — so copy stays signature-centric
  // regardless of which library opened the dialog. (See Prompt 24.1 out-of-scope.)
  return plan === 'pro'
    ? `You've reached the ${cap}-signature limit. Delete one to add another.`
    : 'Upgrade to Pro to save more signatures.';
}

function ListView({
  signatures,
  loading,
  capReached,
  cap,
  plan,
  noun,
  nounPlural,
  applyAllCount,
  onUse,
  onUseForAll,
  onDelete,
  onNew,
}) {
  const fullMessage = capMessage({ plan, cap });
  // Plural forms: "signature"/"signatures" or "initials"/"initials" (uncountable).
  const nounLower = (noun || 'Signature').toLowerCase();
  const nounPluralLower = (nounPlural || 'Signatures').toLowerCase();
  const showApplyAll = applyAllCount > 1;
  return (
    <div className="px-5 pb-5 overflow-y-auto">
      {loading ? (
        <div className="py-12 flex items-center justify-center text-gray-500">
          <Loader2 size={18} className="animate-spin mr-2" /> Loading {nounPluralLower}…
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-gray-500">
              {signatures.length} of {cap} saved
            </p>
          </div>
          <div className="space-y-2 mb-4">
            {signatures.map((sig) => (
              <div
                key={sig.id}
                className="flex items-stretch gap-2 border border-gray-200 rounded-lg hover:border-brand-blue hover:bg-brand-light/10 transition-all overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => onUse(sig)}
                  className="flex-1 flex items-center gap-3 p-3 text-left"
                  title={`Use this ${nounLower} on the current field`}
                >
                  <div className="flex-1 flex items-center justify-center h-16 bg-white rounded">
                    <img
                      src={sig.dataUrl}
                      alt={sig.label || noun}
                      className="max-h-14 max-w-full object-contain"
                    />
                  </div>
                  <div className="shrink-0 text-right">
                    {sig.label && (
                      <div className="text-xs text-gray-600 max-w-[100px] truncate">{sig.label}</div>
                    )}
                    <div className="text-[10px] text-gray-400 uppercase tracking-wide">{sig.kind}</div>
                  </div>
                </button>
                <div className="flex items-center gap-1 pr-2">
                  {showApplyAll && (
                    <button
                      type="button"
                      onClick={() => onUseForAll(sig)}
                      className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-brand-blue bg-brand-light/30 hover:bg-brand-light/60 transition-colors"
                      title={`Apply to all ${applyAllCount} ${nounPluralLower} fields`}
                    >
                      <Copy size={12} />
                      Apply to all {applyAllCount}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => onDelete(e, sig.id)}
                    className="shrink-0 p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    title={`Delete ${nounLower}`}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
            {signatures.length === 0 && (
              <p className="text-sm text-gray-500 py-6 text-center">
                No saved {nounPluralLower} yet. Create one to reuse it across documents.
              </p>
            )}
          </div>

          <button
            onClick={onNew}
            disabled={capReached}
            className="w-full h-11 flex items-center justify-center gap-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-brand-blue hover:text-brand-blue hover:bg-brand-light/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            title={capReached ? fullMessage : `Create a new ${nounLower}`}
          >
            <Plus size={15} /> New {nounLower}
          </button>
          {capReached && (
            <p className="mt-2 text-xs text-gray-500 flex items-center gap-1 justify-center">
              <AlertCircle size={12} /> {fullMessage}
              {plan !== 'pro' && (
                <a
                  href="https://scrivar.com/#pricing"
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-blue hover:underline ml-1"
                >
                  See plans
                </a>
              )}
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ==================== Create view (tabs + save-for-reuse) ====================

function CreateView({ signedIn, capReached, cap, plan, savedCount, noun = 'Signature', onCreated, onSave, onSignInClick }) {
  const [tab, setTab] = useState('draw');
  const [color, setColor] = useState(INK_COLORS[0].value);
  const [saveForReuse, setSaveForReuse] = useState(signedIn && !capReached);
  // Auto-suggested label — user can tweak before saving.
  const defaultLabel = `${noun} ${savedCount + 1}`;
  const [label, setLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Keep Save checkbox in a legal state when the signed-in/cap status changes.
  useEffect(() => {
    if (!signedIn || capReached) setSaveForReuse(false);
  }, [signedIn, capReached]);

  const handleCreated = async ({ dataUrl, kind, strokeWidth }) => {
    if (!dataUrl) return;
    if (signedIn && saveForReuse && !capReached) {
      setSubmitting(true);
      setSaveError(null);
      try {
        await onSave({
          kind,
          label: (label.trim() || defaultLabel).slice(0, 64),
          dataUrl,
          color: kind === 'upload' ? '#000000' : color,
          strokeWidth: kind === 'upload' ? DRAW_BASE_WIDTH : strokeWidth,
        });
      } catch (err) {
        console.error('Save signature failed:', err);
        // Still let the user apply the signature locally — saving is a bonus.
        setSaveError(err.message || 'Could not save signature.');
      } finally {
        setSubmitting(false);
      }
    }
    onCreated(dataUrl);
  };

  return (
    <div className="px-5 pb-5 overflow-y-auto">
      {/* Tabs */}
      <div className="flex gap-1 mb-4">
        {[
          { id: 'draw', label: 'Draw', icon: Pen },
          { id: 'type', label: 'Type', icon: Keyboard },
          { id: 'upload', label: 'Upload', icon: Image },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
              tab === t.id ? 'bg-brand-blue text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Color picker — Draw + Type only */}
      {tab !== 'upload' && <ColorSwatches color={color} onChange={setColor} />}

      {/* Tab content */}
      {tab === 'draw' && <DrawTab color={color} onCreated={handleCreated} noun={noun} />}
      {tab === 'type' && <TypeTab color={color} onCreated={handleCreated} noun={noun} />}
      {tab === 'upload' && <UploadTab onCreated={handleCreated} noun={noun} />}

      {/* Save-for-reuse controls */}
      {signedIn && (
        <div className="mt-4 border-t border-gray-100 pt-3 space-y-2">
          <label
            className={`flex items-center gap-2 text-sm ${
              capReached ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 cursor-pointer'
            }`}
            title={capReached ? capMessage({ plan, cap }) : ''}
          >
            <input
              type="checkbox"
              checked={saveForReuse}
              disabled={capReached}
              onChange={(e) => setSaveForReuse(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            Save for reuse
            {capReached && (
              <span className="text-[11px] text-gray-400 ml-1">— {capMessage({ plan, cap })}</span>
            )}
          </label>
          {saveForReuse && (
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value.slice(0, 64))}
              placeholder={defaultLabel}
              className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-brand-blue"
            />
          )}
          {submitting && (
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <Loader2 size={12} className="animate-spin" /> Saving…
            </p>
          )}
          {saveError && (
            <p className="text-xs text-red-600 flex items-center gap-1">
              <AlertCircle size={12} /> {saveError}
            </p>
          )}
        </div>
      )}

      {!signedIn && (
        <p className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500 text-center">
          <button onClick={onSignInClick} className="text-brand-blue hover:underline">
            Sign in
          </button>{' '}
          to save signatures and reuse them across documents.
        </p>
      )}
    </div>
  );
}

// ==================== Color swatches ====================

function ColorSwatches({ color, onChange }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="text-xs text-gray-500">Color</span>
      <div className="flex gap-1.5">
        {INK_COLORS.map((c) => {
          const selected = c.value === color;
          return (
            <button
              key={c.value}
              onClick={() => onChange(c.value)}
              aria-label={c.label}
              aria-pressed={selected}
              className={`w-7 h-7 rounded-full border-2 transition-all flex items-center justify-center ${
                selected ? 'border-gray-900 ring-2 ring-offset-2 ring-gray-900/20' : 'border-gray-200 hover:border-gray-400'
              }`}
              style={{ backgroundColor: c.value }}
              title={c.label}
            >
              {selected && (
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: c.value === '#000000' ? '#ffffff' : '#ffffff' }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ==================== Draw tab ====================

function DrawTab({ color, onCreated, noun = 'Signature' }) {
  // Initials rarely fill an 800×300 canvas — the tiny mark disappears in all
  // that whitespace. Squarer bitmap + tighter CSS box keeps the stroke legible
  // and makes the trim step produce a tidier image.
  const isInitials = noun === 'Initials';
  const canvasWidth = isInitials ? 320 : 800;
  const canvasHeight = isInitials ? 200 : 300;

  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const pointsRef = useRef([]);
  const lastSampleRef = useRef(null); // { x, y, t } — for velocity-based taper

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDraw = (e) => {
    setIsDrawing(true);
    const pos = getPos(e);
    pointsRef.current = [pos];
    lastSampleRef.current = { x: pos.x, y: pos.y, t: performance.now() };
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const pos = getPos(e);
    pointsRef.current.push(pos);
    const pts = pointsRef.current;

    if (pts.length < 3) return;

    // Velocity-based taper — slow strokes thicken up to 60% beyond base,
    // fast strokes stay at base. Reads more like ink than pencil.
    const now = performance.now();
    const last = lastSampleRef.current;
    let width = DRAW_BASE_WIDTH;
    if (last) {
      const dx = pos.x - last.x;
      const dy = pos.y - last.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const dt = Math.max(1, now - last.t); // avoid div-by-zero
      const speed = dist / dt; // px per ms
      width = DRAW_BASE_WIDTH * (1 + (1 - Math.min(speed / 1.5, 1)) * 0.6);
    }
    lastSampleRef.current = { x: pos.x, y: pos.y, t: now };

    // Smooth curve using the last 3 sample points. Each segment is its own
    // sub-path so the taper-varying lineWidth actually renders.
    const p1 = pts[pts.length - 3];
    const p2 = pts[pts.length - 2];
    const p3 = pts[pts.length - 1];
    const mid = { x: (p2.x + p3.x) / 2, y: (p2.y + p3.y) / 2 };

    const ctx = canvasRef.current.getContext('2d');
    ctx.lineWidth = width;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = color;

    ctx.beginPath();
    ctx.moveTo((p1.x + p2.x) / 2, (p1.y + p2.y) / 2);
    ctx.quadraticCurveTo(p2.x, p2.y, mid.x, mid.y);
    ctx.stroke();

    setHasContent(true);
  };

  const endDraw = () => {
    setIsDrawing(false);
    pointsRef.current = [];
    lastSampleRef.current = null;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setHasContent(false);
  };

  const confirm = () => {
    const dataUrl = trimCanvas(canvasRef.current);
    if (dataUrl) onCreated({ dataUrl, kind: 'draw', strokeWidth: DRAW_BASE_WIDTH });
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={canvasWidth}
        height={canvasHeight}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        className={
          isInitials
            ? 'mx-auto block h-40 w-full max-w-[16rem] border-2 border-dashed border-gray-300 rounded-xl cursor-crosshair bg-white'
            : 'w-full h-40 border-2 border-dashed border-gray-300 rounded-xl cursor-crosshair bg-white'
        }
      />
      <p className="text-xs text-gray-400 text-center mt-2">Draw your {noun.toLowerCase()} above</p>
      <div className="flex gap-3 mt-4">
        <button
          onClick={clear}
          className="flex-1 h-10 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
        >
          Clear
        </button>
        <button
          onClick={confirm}
          disabled={!hasContent}
          className="flex-1 h-10 bg-brand-blue text-white rounded-lg text-sm font-medium hover:bg-brand-dark transition-colors disabled:opacity-50"
        >
          Use {noun}
        </button>
      </div>
    </div>
  );
}

// ==================== Type tab ====================

function TypeTab({ color, onCreated, noun = 'Signature' }) {
  const isInitials = noun === 'Initials';
  // Initials are conventionally 2–3 characters. Let users land up to 6 in case
  // of middle names, but no more — the preview and the rendered bitmap fall
  // apart past that. Signatures stay uncapped here (the schema enforces 64).
  const maxLength = isInitials ? 6 : undefined;
  const placeholder = isInitials ? 'Type your initials' : 'Type your name';
  const placeholderPreview = isInitials ? 'ABC' : 'Your Name';
  const previewFontSize = isInitials ? '36px' : '24px';

  const [text, setText] = useState('');
  const [selectedFont, setSelectedFont] = useState(FONTS[0].name);

  const confirm = () => {
    if (!text.trim()) return;
    const dataUrl = renderTextSignature(text.trim(), selectedFont, color);
    if (dataUrl) onCreated({ dataUrl, kind: 'type', strokeWidth: TYPE_STROKE_WIDTH });
  };

  return (
    <div>
      <input
        type="text"
        value={text}
        onChange={(e) => {
          const next = maxLength ? e.target.value.slice(0, maxLength) : e.target.value;
          setText(next);
        }}
        maxLength={maxLength}
        placeholder={placeholder}
        className="w-full h-10 px-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-brand-blue mb-4"
        autoFocus
      />

      <div className="grid grid-cols-1 gap-2 mb-4">
        {FONTS.map((f) => (
          <button
            key={f.name}
            onClick={() => setSelectedFont(f.name)}
            className={`flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all ${
              selectedFont === f.name
                ? 'border-brand-blue bg-brand-light/20'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <span
              style={{ fontFamily: `"${f.name}", cursive`, fontSize: previewFontSize, color }}
            >
              {text || placeholderPreview}
            </span>
            <span className="text-xs text-gray-400">{f.label}</span>
          </button>
        ))}
      </div>

      <button
        onClick={confirm}
        disabled={!text.trim()}
        className="w-full h-10 bg-brand-blue text-white rounded-lg text-sm font-medium hover:bg-brand-dark transition-colors disabled:opacity-50"
      >
        Use {noun}
      </button>
    </div>
  );
}

// ==================== Upload tab ====================

function UploadTab({ onCreated, noun = 'Signature' }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);

  const handleFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <div>
      {!preview ? (
        <div
          onClick={() => inputRef.current?.click()}
          onDrop={(e) => {
            e.preventDefault();
            handleFile(e.dataTransfer.files[0]);
          }}
          onDragOver={(e) => e.preventDefault()}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-brand-blue hover:bg-gray-50 transition-all"
        >
          <Image size={32} className="mx-auto mb-2 text-gray-400" />
          <p className="text-sm text-gray-600">Drop a {noun.toLowerCase()} image or click to browse</p>
          <p className="text-xs text-gray-400 mt-1">PNG or JPG</p>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg"
            onChange={(e) => handleFile(e.target.files[0])}
            className="hidden"
          />
        </div>
      ) : (
        <>
          <div className="border-2 border-gray-200 rounded-xl p-4 mb-4 bg-white flex items-center justify-center">
            <img src={preview} alt={noun} className="max-h-32 max-w-full object-contain" />
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setPreview(null)}
              className="flex-1 h-10 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Choose different
            </button>
            <button
              onClick={() => onCreated({ dataUrl: preview, kind: 'upload', strokeWidth: DRAW_BASE_WIDTH })}
              className="flex-1 h-10 bg-brand-blue text-white rounded-lg text-sm font-medium hover:bg-brand-dark transition-colors"
            >
              Use {noun}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ==================== Helpers ====================

function trimCanvas(canvas) {
  const ctx = canvas.getContext('2d');
  const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);

  let top = height, bottom = 0, left = width, right = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > 0) {
        if (y < top) top = y;
        if (y > bottom) bottom = y;
        if (x < left) left = x;
        if (x > right) right = x;
      }
    }
  }

  if (top > bottom) return null;

  const pad = 10;
  const tw = right - left + 1 + pad * 2;
  const th = bottom - top + 1 + pad * 2;
  const trimmed = document.createElement('canvas');
  trimmed.width = tw;
  trimmed.height = th;
  trimmed.getContext('2d').drawImage(canvas, left - pad, top - pad, tw, th, 0, 0, tw, th);

  return trimmed.toDataURL('image/png');
}

function renderTextSignature(text, fontFamily, color) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const fontSize = 64;

  ctx.font = `${fontSize}px "${fontFamily}", cursive`;
  const metrics = ctx.measureText(text);

  const pad = 16;
  canvas.width = Math.ceil(metrics.width) + pad * 2;
  canvas.height = Math.ceil(fontSize * 1.4) + pad * 2;

  ctx.font = `${fontSize}px "${fontFamily}", cursive`;
  ctx.fillStyle = color;
  ctx.textBaseline = 'middle';
  ctx.fillText(text, pad, canvas.height / 2);

  return canvas.toDataURL('image/png');
}
